import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NgxCurrencyDirective } from 'ngx-currency';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AlertCardComponent } from '../../../../shared/components/alert-card/alert-card.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoProcessoKanbanEnum } from '../../../../shared/enums/vendas/tipo-processo-kanban.enum';
import { OpcaoTransicaoEtapaEnum } from '../../../../shared/enums/vendas/opcao-transicao-etapa.enum';
import { ModoAoVencerAutomacaoVendaEnum } from '../../../../shared/enums/vendas/modo-ao-vencer-automacao-venda.enum';
import { TipoDestinatarioAutomacaoVendaEnum } from '../../../../shared/enums/vendas/tipo-destinatario-automacao-venda.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { AutomacaoVendaService } from '../../../../shared/services/apis/vendas/automacao-venda.service';
import { ConfigKanbanService } from '../../../../shared/services/apis/vendas/config-kanban.service';
import { PerfilService } from '../../../../shared/services/apis/usuario/perfil.service';
import { UsuarioService } from '../../../../shared/services/apis/usuario/usuario.service';
import { AutomacaoVendaSalvarRequest } from '../../../../shared/services/requests/vendas/automacao-venda-salvar.request';
import { GetAutomacaoVendaResponse } from '../../../../shared/services/responses/vendas/get-automacao-venda.response';
import { AutomacaoVendaDestinatarioStruct } from '../../../../shared/services/structs/vendas/automacao-venda-destinatario.struct';
import { AutomacaoVendaOpcaoStruct } from '../../../../shared/services/structs/vendas/automacao-venda-opcao.struct';
import { DestinatarioOpcaoStruct } from '../../../../shared/services/structs/vendas/destinatario-opcao.struct';
import { KanbanProcessoStruct } from '../../../../shared/services/structs/vendas/kanban-processo.struct';
import { EtapaKanbanStruct } from '../../../../shared/services/structs/vendas/etapa-kanban.struct';

const AVISOS_DEPENDENCIA: { ativoCtrl: string; dependeDeCtrl: string; texto: string }[] = [
  { ativoCtrl: 'isAtivoAutorizacaoNFe', dependeDeCtrl: 'isAtivoEmissaoNFe', texto: 'Depende de "Emitir Nota Fiscal Eletrônica (NF-e)", que está desativada.' },
  { ativoCtrl: 'isAtivoEtiquetaEnvio', dependeDeCtrl: 'isAtivoEnvioSeparacao', texto: 'A embalagem só é concluída após a separação, hoje desativada.' },
];

/** Descrição do campo pendente por controle — usada pra montar o toast de "Configuração
 *  incompleta" com o texto certo (gatilho/prazo/destinatário), não sempre "gatilho". */
const CAMPO_INCOMPLETO_POR_CTRL: Record<string, string> = {
  gatilhoEmissaoNFe: 'o gatilho de "Emitir Nota Fiscal Eletrônica (NF-e)"',
  gatilhoAutorizacaoNFe: 'o gatilho de "Autorizar Nota Fiscal Eletrônica (NF-e) na Sefaz"',
  gatilhoSaidaEstoque: 'o gatilho de "Lançar saída de estoque"',
  idOpcaoEtiquetaEnvio: 'o gatilho de "Imprimir etiqueta de envio"',
  gatilhoEnvioSeparacao: 'o gatilho de "Enviar para separação"',
  gatilhoEnvioExpedicao: 'o gatilho de "Enviar para expedição"',
  gatilhoGeracaoPedido: 'o gatilho de "Gerar pedido de venda automaticamente"',
  gatilhoReservaEstoqueOrcamento: 'o gatilho de "Reservar estoque do orçamento"',
  gatilhoReservaEstoquePedido: 'o gatilho de "Reservar estoque do pedido de venda"',
  idOpcaoAvisoRecebimentoAtraso: 'o gatilho de "Aviso de contas a receber em atraso"',
  gatilhoAprovacaoPagamento: 'a etapa de destino de "Ao identificar o pagamento"',

  idModoAoVencerLiberacaoReservaPedido: 'a ação ao vencer de "Liberar reserva do pedido"',
  diasLiberacaoReservaPedido: 'o prazo (dias) de "Liberar reserva do pedido"',
  destinatariosLiberacaoReservaPedido: 'os destinatários de "Liberar reserva do pedido"',
};

class MostrarErroImediatoMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return !!control?.invalid;
  }
}

@Component({
  selector: 'app-automacoes-venda',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, FormsModule, RouterModule, HeaderComponent, LoadingBoxComponent,
    AlertCardComponent, FooterActionComponent, MatButtonModule, MatFormFieldModule,
    MatIconModule, MatInputModule, MatSelectModule, MatSlideToggleModule,
    MatTooltipModule, NgxCurrencyDirective
  ],
  templateUrl: './automacoes-venda.component.html',
  styleUrls: ['./automacoes-venda.component.css'],
  // Nossa validação marca os controles inválidos via validarCompletude() (setErrors), não por
  // interação do usuário — o ErrorStateMatcher padrão do Material só mostra <mat-error> depois
  // do controle ficar "touched", então sem isso a borda vermelha aparecia mas a mensagem nunca.
  providers: [{ provide: ErrorStateMatcher, useClass: MostrarErroImediatoMatcher }],
})
export class AutomacoesVendaComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public tipoProcessoKanbanEnum = TipoProcessoKanbanEnum;
  public modoAoVencerEnum = ModoAoVencerAutomacaoVendaEnum;
  public opcaoTransicaoEtapaEnum = OpcaoTransicaoEtapaEnum;

  public isLoading = false;
  public salvando = false;
  public mostrarComoFunciona = false;
  /** Só true quando a empresa realmente não tem nenhuma automação ativa — controla a frase
   *  "Nenhuma automação está ativa", que não pode aparecer só porque o usuário clicou em
   *  "Como funciona" com automações já configuradas. */
  public nenhumaAutomacaoAtiva = false;

  public opcoesAoVencer: AutomacaoVendaOpcaoStruct[] = [];

  public integerMask: any;

  public destinatariosDoCard: DestinatarioOpcaoStruct[] = [{ valor: 'card', label: 'Responsável pelo card', icon: 'assignment_ind', dinamico: true }];
  public destinatariosPorPerfil: DestinatarioOpcaoStruct[] = [];
  public destinatariosPorUsuario: DestinatarioOpcaoStruct[] = [];

  public model: FormGroup;
  public respostaAtual: GetAutomacaoVendaResponse | null = null;
  public trabalhaComSeparacao = true;
  public trabalhaComExpedicao = true;
  private etapasPorProcesso = new Map<number, EtapaKanbanStruct[]>();
  public buscaDestinatarioPedido = '';

  constructor(
    private formBuilder: FormBuilder,
    private automacaoVendaService: AutomacaoVendaService,
    private configKanbanService: ConfigKanbanService,
    private perfilService: PerfilService,
    private usuarioService: UsuarioService,
    private alertService: AlertService,
    private maskService: MaskService,
  ) {
    this.integerMask = { ...this.maskService.getIntegerMask(), allowNegative: false };
  }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      isAtivoEmissaoNFe: [false], gatilhoEmissaoNFe: [null],
      isAtivoAutorizacaoNFe: [false], gatilhoAutorizacaoNFe: [null],
      isAtivoSaidaEstoque: [false], gatilhoSaidaEstoque: [null],
      isAtivoEtiquetaEnvio: [false], idOpcaoEtiquetaEnvio: [null], // Mantém simples, pois não tem Kanban
      isAtivoEnvioSeparacao: [false], gatilhoEnvioSeparacao: [null],
      isAtivoEnvioExpedicao: [false], gatilhoEnvioExpedicao: [null],
      isAtivoGeracaoPedido: [false], gatilhoGeracaoPedido: [null],
      isAtivoReservaEstoqueOrcamento: [false], gatilhoReservaEstoqueOrcamento: [null],
      isAtivoReservaEstoquePedido: [false], gatilhoReservaEstoquePedido: [null],
      isAtivoAvisoRecebimentoAtraso: [false], idOpcaoAvisoRecebimentoAtraso: [null], // Mantém simples

      // Blocos de validade/tempo
      isAtivoLiberacaoReservaPedido: [false],
      idModoAoVencerLiberacaoReservaPedido: [null],
      diasLiberacaoReservaPedido: [null],
      destinatariosLiberacaoReservaPedido: [[]],

      isAtivoPrimeiraVenda: [false],
      isAtivoTagsNotaFiscal: [false],

      isAtivoAprovacaoPagamento: [false],
      gatilhoAprovacaoPagamento: [null],

    }, { validators: this.validarCompletude() });

    this.configurarComportamentoDosToggles();
    this.carregar();
  }

  private validarCompletude(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const v = group.value;
      const marcar = (ctrlName: string, invalido: boolean) => group.get(ctrlName)?.setErrors(invalido ? { incompleta: true } : null);

      const comOpcao = (ativoCtrl: string, opcaoCtrl: string) => marcar(opcaoCtrl, v[ativoCtrl] && v[opcaoCtrl] == null);
      const comGatilho = (ativoCtrl: string, gatilhoCtrl: string) => marcar(gatilhoCtrl, v[ativoCtrl] && !v[gatilhoCtrl]);

      const tempoDecorrido = (ativoCtrl: string, modoCtrl: string, diasCtrl: string, destinatariosCtrl: string) => {
        const ativo = v[ativoCtrl];
        marcar(modoCtrl, ativo && v[modoCtrl] == null);
        marcar(diasCtrl, ativo && (v[diasCtrl] == null || v[diasCtrl] <= 0));
        marcar(destinatariosCtrl, ativo && v[modoCtrl] === ModoAoVencerAutomacaoVendaEnum.ApenasNotificar && !v[destinatariosCtrl]?.length);
      };

      comGatilho('isAtivoEmissaoNFe', 'gatilhoEmissaoNFe');
      comGatilho('isAtivoAutorizacaoNFe', 'gatilhoAutorizacaoNFe');
      comGatilho('isAtivoSaidaEstoque', 'gatilhoSaidaEstoque');
      comOpcao('isAtivoEtiquetaEnvio', 'idOpcaoEtiquetaEnvio');
      comGatilho('isAtivoEnvioSeparacao', 'gatilhoEnvioSeparacao');
      comGatilho('isAtivoEnvioExpedicao', 'gatilhoEnvioExpedicao');
      comGatilho('isAtivoGeracaoPedido', 'gatilhoGeracaoPedido');
      comGatilho('isAtivoReservaEstoqueOrcamento', 'gatilhoReservaEstoqueOrcamento');
      comGatilho('isAtivoReservaEstoquePedido', 'gatilhoReservaEstoquePedido');
      comOpcao('isAtivoAvisoRecebimentoAtraso', 'idOpcaoAvisoRecebimentoAtraso');
      comGatilho('isAtivoAprovacaoPagamento', 'gatilhoAprovacaoPagamento');

      tempoDecorrido('isAtivoLiberacaoReservaPedido', 'idModoAoVencerLiberacaoReservaPedido', 'diasLiberacaoReservaPedido', 'destinatariosLiberacaoReservaPedido');

      return null;
    };
  }

  private carregar(): void {
    this.isLoading = true;

    forkJoin({
      automacoes: this.automacaoVendaService.get().pipe(catchError(() => of(null))),
      kanban: this.configKanbanService.get().pipe(catchError(() => of(null))),
      perfis: this.perfilService.getAllLookup().pipe(catchError(() => of(null))),
      usuarios: this.usuarioService.getAll().pipe(catchError(() => of(null))),
    }).subscribe(({ automacoes, kanban, perfis, usuarios }) => {
      this.isLoading = false;

      if (perfis && !perfis.isError) {
        this.destinatariosPorPerfil = (perfis.listaPerfil ?? []).map((item: any) => item.perfil)
          .map((p: any) => ({ valor: `perfil:${p.idPerfil}`, label: p.nome, icon: 'badge', dinamico: false }));
      }

      if (usuarios && !usuarios.isError) {
        this.destinatariosPorUsuario = (usuarios.listaUsuario ?? [])
          .map((u: any) => ({ valor: `usuario:${u.idUsuario}`, label: u.nomeUsuario, icon: 'person', dinamico: false }));
      }

      if (kanban && !kanban.isError) {
        this.trabalhaComSeparacao = kanban.isTrabalhaComSeparacao;
        this.trabalhaComExpedicao = kanban.isTrabalhaComExpedicao;
        this.etapasPorProcesso = new Map(
          (kanban.processos ?? []).map((p: KanbanProcessoStruct) => [
            p.idTipoProcessoKanban,
            (p.etapas ?? []).slice().sort((a, b) => a.ordem - b.ordem),
          ]),
        );
      }

      if (!automacoes || automacoes.isError) {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Erro ao carregar automações', AlertType.warning);
        return;
      }

      this.aplicarConfiguracao(automacoes);
    });
  }

  private aplicarConfiguracao(res: GetAutomacaoVendaResponse): void {
    this.respostaAtual = res;
    this.opcoesAoVencer = res.modoAoVencer;
    const config = res.config ?? {};

    // 1. Aplica todos os campos booleanos e opções simples que vieram do backend
    this.model.patchValue(config);

    // 2. Monta os objetos de gatilho baseados nas FKs que vieram do backend
    this.model.patchValue({
      gatilhoEmissaoNFe: this.criarObjetoGatilho(config.idOpcaoEmissaoNFe, config.idEtapaKanbanEmissaoNFe),
      gatilhoAutorizacaoNFe: this.criarObjetoGatilho(config.idOpcaoAutorizacaoNFe, config.idEtapaKanbanAutorizacaoNFe),
      gatilhoSaidaEstoque: this.criarObjetoGatilho(config.idOpcaoSaidaEstoque, config.idEtapaKanbanSaidaEstoque),
      gatilhoEnvioSeparacao: this.criarObjetoGatilho(config.idOpcaoEnvioSeparacao, config.idEtapaKanbanEnvioSeparacao),
      gatilhoEnvioExpedicao: this.criarObjetoGatilho(config.idOpcaoEnvioExpedicao, config.idEtapaKanbanEnvioExpedicao),
      gatilhoGeracaoPedido: this.criarObjetoGatilho(config.idOpcaoGeracaoPedido, config.idEtapaKanbanGeracaoPedido),
      gatilhoReservaEstoqueOrcamento: this.criarObjetoGatilho(config.idOpcaoReservaEstoqueOrcamento, config.idEtapaKanbanReservaEstoqueOrcamento),
      gatilhoReservaEstoquePedido: this.criarObjetoGatilho(config.idOpcaoReservaEstoquePedido, config.idEtapaKanbanReservaEstoquePedido),
      gatilhoAprovacaoPagamento: this.criarObjetoGatilho(config.idOpcaoAprovacaoPagamento, config.idEtapaKanbanAprovacaoPagamento),

      ...this.destinatariosParaForm('LiberacaoReservaPedido', res.destinatariosPedido)
    });

    this.model.markAsPristine();

    const v = this.model.value;
    this.nenhumaAutomacaoAtiva = !(
      v.isAtivoEmissaoNFe || v.isAtivoAutorizacaoNFe || v.isAtivoSaidaEstoque || v.isAtivoEtiquetaEnvio
      || v.isAtivoEnvioSeparacao || v.isAtivoEnvioExpedicao || v.isAtivoGeracaoPedido
      || v.isAtivoReservaEstoqueOrcamento || v.isAtivoReservaEstoquePedido
      || v.isAtivoLiberacaoReservaPedido || v.isAtivoAvisoRecebimentoAtraso || v.isAtivoPrimeiraVenda
      || v.isAtivoTagsNotaFiscal || v.isAtivoAprovacaoPagamento
    );
    // Só entra na tela "Como funciona" automaticamente no primeiro acesso de verdade — depois
    // disso, quem abre é o botão manual (aoVerComoFunciona), que nunca mexe nessa flag de novo.
    this.mostrarComoFunciona = this.nenhumaAutomacaoAtiva;
  }

  /** Helper para transformar os IDs em objeto apenas se existir um dos dois */
  private criarObjetoGatilho(idOpcao?: number | null, idEtapaKanban?: number | null) {
    if (idOpcao == null && idEtapaKanban == null) return null;
    return { idOpcao: idOpcao ?? null, idEtapaKanban: idEtapaKanban ?? null };
  }

  private destinatariosParaForm(key: string, lista: AutomacaoVendaDestinatarioStruct[]): Record<string, unknown> {
    return {
      ['destinatarios' + key]: (lista ?? []).map(d => {
        if (d.idTipoDestinatarioAutomacaoVenda === TipoDestinatarioAutomacaoVendaEnum.ResponsavelPeloCard) return 'card';
        if (d.idTipoDestinatarioAutomacaoVenda === TipoDestinatarioAutomacaoVendaEnum.Perfil) return `perfil:${d.idPerfil}`;
        return `usuario:${d.idUsuario}`;
      }),
    };
  }

  public etapasDoProcesso(idTipoProcessoKanban: TipoProcessoKanbanEnum): EtapaKanbanStruct[] {
    return this.etapasPorProcesso.get(idTipoProcessoKanban) ?? [];
  }

  public avisoDependencia(ativoCtrl: string): string {
    if (!this.model.get(ativoCtrl)?.value) return '';
    const regra = AVISOS_DEPENDENCIA.find(a => a.ativoCtrl === ativoCtrl);
    if (regra && !this.model.get(regra.dependeDeCtrl)?.value) return regra.texto;
    return '';
  }

  public metaDestinatario(chave: string): DestinatarioOpcaoStruct {
    const conhecido = [...this.destinatariosDoCard, ...this.destinatariosPorPerfil, ...this.destinatariosPorUsuario]
      .find(o => o.valor === chave);
    return conhecido ?? { valor: chave, label: chave, icon: 'badge', dinamico: false };
  }

  public aoComecar(): void {
    this.mostrarComoFunciona = false;
  }

  public aoVerComoFunciona(): void {
    this.mostrarComoFunciona = true;
  }

  public get temAlteracoes(): boolean {
    return this.model.dirty;
  }

  private montarRequest(): AutomacaoVendaSalvarRequest {
    const valor = this.model.getRawValue();

    // Reset explícito para opções simples e lógicas de tempo
    if (!valor.isAtivoEtiquetaEnvio) { valor.idOpcaoEtiquetaEnvio = null; }
    if (!valor.isAtivoLiberacaoReservaPedido) { valor.idModoAoVencerLiberacaoReservaPedido = null; valor.diasLiberacaoReservaPedido = null; }
    if (!valor.isAtivoAvisoRecebimentoAtraso) { valor.idOpcaoAvisoRecebimentoAtraso = null; }

    const { destinatariosLiberacaoReservaPedido, ...config } = valor;

    return {
      ...config,
      destinatariosPedido: valor.isAtivoLiberacaoReservaPedido ? this.montarDestinatarios(destinatariosLiberacaoReservaPedido) : [],

      // Desmembrando os objetos de volta para FKs independentes (Backend)
      idOpcaoEmissaoNFe: valor.isAtivoEmissaoNFe ? valor.gatilhoEmissaoNFe?.idOpcao : null,
      idEtapaKanbanEmissaoNFe: valor.isAtivoEmissaoNFe ? valor.gatilhoEmissaoNFe?.idEtapaKanban : null,

      idOpcaoAutorizacaoNFe: valor.isAtivoAutorizacaoNFe ? valor.gatilhoAutorizacaoNFe?.idOpcao : null,
      idEtapaKanbanAutorizacaoNFe: valor.isAtivoAutorizacaoNFe ? valor.gatilhoAutorizacaoNFe?.idEtapaKanban : null,

      idOpcaoSaidaEstoque: valor.isAtivoSaidaEstoque ? valor.gatilhoSaidaEstoque?.idOpcao : null,
      idEtapaKanbanSaidaEstoque: valor.isAtivoSaidaEstoque ? valor.gatilhoSaidaEstoque?.idEtapaKanban : null,

      idOpcaoEnvioSeparacao: valor.isAtivoEnvioSeparacao ? valor.gatilhoEnvioSeparacao?.idOpcao : null,
      idEtapaKanbanEnvioSeparacao: valor.isAtivoEnvioSeparacao ? valor.gatilhoEnvioSeparacao?.idEtapaKanban : null,

      idOpcaoEnvioExpedicao: valor.isAtivoEnvioExpedicao ? valor.gatilhoEnvioExpedicao?.idOpcao : null,
      idEtapaKanbanEnvioExpedicao: valor.isAtivoEnvioExpedicao ? valor.gatilhoEnvioExpedicao?.idEtapaKanban : null,

      idOpcaoGeracaoPedido: valor.isAtivoGeracaoPedido ? valor.gatilhoGeracaoPedido?.idOpcao : null,
      idEtapaKanbanGeracaoPedido: valor.isAtivoGeracaoPedido ? valor.gatilhoGeracaoPedido?.idEtapaKanban : null,

      idOpcaoReservaEstoqueOrcamento: valor.isAtivoReservaEstoqueOrcamento ? valor.gatilhoReservaEstoqueOrcamento?.idOpcao : null,
      idEtapaKanbanReservaEstoqueOrcamento: valor.isAtivoReservaEstoqueOrcamento ? valor.gatilhoReservaEstoqueOrcamento?.idEtapaKanban : null,

      idOpcaoReservaEstoquePedido: valor.isAtivoReservaEstoquePedido ? valor.gatilhoReservaEstoquePedido?.idOpcao : null,
      idEtapaKanbanReservaEstoquePedido: valor.isAtivoReservaEstoquePedido ? valor.gatilhoReservaEstoquePedido?.idEtapaKanban : null,

      idOpcaoAprovacaoPagamento: valor.isAtivoAprovacaoPagamento ? valor.gatilhoAprovacaoPagamento?.idOpcao : null,
      idEtapaKanbanAprovacaoPagamento: valor.isAtivoAprovacaoPagamento ? valor.gatilhoAprovacaoPagamento?.idEtapaKanban : null,
    };
  }

  private montarDestinatarios(chaves: string[]): AutomacaoVendaDestinatarioStruct[] {
    return (chaves ?? []).map(chave => {
      if (chave === 'card') return { idTipoDestinatarioAutomacaoVenda: TipoDestinatarioAutomacaoVendaEnum.ResponsavelPeloCard, idPerfil: null, idUsuario: null };
      if (chave.startsWith('perfil:')) return { idTipoDestinatarioAutomacaoVenda: TipoDestinatarioAutomacaoVendaEnum.Perfil, idPerfil: Number(chave.slice(7)), idUsuario: null };
      return { idTipoDestinatarioAutomacaoVenda: TipoDestinatarioAutomacaoVendaEnum.Usuario, idPerfil: null, idUsuario: Number(chave.slice(8)) };
    });
  }

  public descartar(): void {
    this.carregar();
  }

  public salvar(): void {
    this.model.updateValueAndValidity();
    if (this.model.invalid) {
      const pendencias = Object.keys(this.model.controls)
        .filter(nome => this.model.get(nome)?.invalid)
        .map(nome => CAMPO_INCOMPLETO_POR_CTRL[nome])
        .filter((texto): texto is string => !!texto);
      this.alertService.show('Configuração incompleta', `Defina ${pendencias.join(', ')}.`, AlertType.error);
      return;
    }

    this.salvando = true;
    this.automacaoVendaService.salvar(this.montarRequest()).subscribe({
      next: res => {
        this.salvando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível salvar as automações.', AlertType.error);
          return;
        }
        this.alertService.show('Sucesso', 'Automações salvas. As automações ativas passam a valer para os próximos documentos.', AlertType.success);
        this.carregar();
      },
      error: () => {
        this.salvando = false;
        this.alertService.show('Erro', 'Não foi possível salvar as automações.', AlertType.error);
      },
    });
  }

  private configurarComportamentoDosToggles(): void {
    const dependencias = [
      { toggle: 'isAtivoEmissaoNFe', campos: ['gatilhoEmissaoNFe'] },
      { toggle: 'isAtivoAutorizacaoNFe', campos: ['gatilhoAutorizacaoNFe'] },
      { toggle: 'isAtivoSaidaEstoque', campos: ['gatilhoSaidaEstoque'] },
      { toggle: 'isAtivoEtiquetaEnvio', campos: ['idOpcaoEtiquetaEnvio'] },
      { toggle: 'isAtivoEnvioSeparacao', campos: ['gatilhoEnvioSeparacao'] },
      { toggle: 'isAtivoEnvioExpedicao', campos: ['gatilhoEnvioExpedicao'] },
      { toggle: 'isAtivoGeracaoPedido', campos: ['gatilhoGeracaoPedido'] },
      { toggle: 'isAtivoReservaEstoqueOrcamento', campos: ['gatilhoReservaEstoqueOrcamento'] },
      { toggle: 'isAtivoReservaEstoquePedido', campos: ['gatilhoReservaEstoquePedido'] },
      { toggle: 'isAtivoLiberacaoReservaPedido', campos: ['idModoAoVencerLiberacaoReservaPedido', 'diasLiberacaoReservaPedido', 'destinatariosLiberacaoReservaPedido'] },
      { toggle: 'isAtivoAvisoRecebimentoAtraso', campos: ['idOpcaoAvisoRecebimentoAtraso'] },
      { toggle: 'isAtivoAprovacaoPagamento', campos: ['gatilhoAprovacaoPagamento'] }
    ];

    dependencias.forEach(({ toggle, campos }) => {
      const toggleCtrl = this.model.get(toggle);

      if (!toggleCtrl?.value) {
        campos.forEach(c => this.model.get(c)?.disable({ emitEvent: false }));
      }

      toggleCtrl?.valueChanges.subscribe(isAtivo => {
        campos.forEach(c => {
          const ctrl = this.model.get(c);

          if (isAtivo) {
            ctrl?.enable();
          } else {
            ctrl?.disable();
          }
        });
      });
    });
  }

  public compararGatilhos(a: any, b: any): boolean {
    if (!a || !b) return false;
    return a.idOpcao === b.idOpcao && a.idEtapaKanban === b.idEtapaKanban;
  }

  public filtrarDestinatarios(
    categoriaAtual: DestinatarioOpcaoStruct[],
    termoBusca: string,
    selecionados: string[] | null | undefined
  ): DestinatarioOpcaoStruct[] {
    const arrSelecionados = selecionados || [];

    if (!termoBusca) return categoriaAtual;

    const termo = termoBusca.toLowerCase();

    // Junta todas as categorias para verificar se existe alguma correspondência global
    const todosDestinatarios = [
      ...this.destinatariosDoCard,
      ...this.destinatariosPorPerfil,
      ...this.destinatariosPorUsuario
    ];

    const temCorrespondenciaGlobal = todosDestinatarios.some(d => d.label.toLowerCase().includes(termo));

    // Se não achou em NENHUMA lista, retorna a categoria atual inteira
    if (!temCorrespondenciaGlobal) return categoriaAtual;

    // Se achou, retorna apenas quem bate com o termo (ou que já está selecionado) da categoria ATUAL
    return categoriaAtual.filter(d =>
      d.label.toLowerCase().includes(termo) || arrSelecionados.includes(d.valor)
    );
  }

  // Estado de visibilidade das seções (iniciam abertas)
  public secoesAbertas = {
    faturamento: true,
    orcamento: true,
    pedido: true,
  };

  public toggleSecao(secao: 'faturamento' | 'orcamento' | 'pedido'): void {
    this.secoesAbertas[secao] = !this.secoesAbertas[secao];
  }

  // Getters para os contadores dinamicos de automações ativas
  public get ativasFaturamento(): string {
    const v = this.model.value;
    // Base: emissao, autorizacao, estoque
    let total = 3;
    let ativas = (v.isAtivoEmissaoNFe ? 1 : 0) +
      (v.isAtivoAutorizacaoNFe ? 1 : 0) +
      (v.isAtivoSaidaEstoque ? 1 : 0);

    if (this.trabalhaComExpedicao) {
      total++;
      if (v.isAtivoEtiquetaEnvio) ativas++;
      total++;
      if (v.isAtivoEnvioExpedicao) ativas++;
    }
    if (this.trabalhaComSeparacao) {
      total++;
      if (v.isAtivoEnvioSeparacao) ativas++;
    }
    return `${ativas} de ${total} ativas`;
  }

  public get ativasOrcamento(): string {
    const v = this.model.value;
    const ativas = (v.isAtivoGeracaoPedido ? 1 : 0) +
      (v.isAtivoReservaEstoqueOrcamento ? 1 : 0);
    return `${ativas} de 2 ativas`;
  }

  public get ativasPedido(): string {
    const v = this.model.value;
    const ativas = (v.isAtivoReservaEstoquePedido ? 1 : 0) +
      (v.isAtivoLiberacaoReservaPedido ? 1 : 0) +
      (v.isAtivoAvisoRecebimentoAtraso ? 1 : 0) + // <--- Veio para cá
      (v.isAtivoPrimeiraVenda ? 1 : 0) +
      (v.isAtivoTagsNotaFiscal ? 1 : 0) +
      (v.isAtivoAprovacaoPagamento ? 1 : 0);
    return `${ativas} de 6 ativas`;
  }
}